/**
 * Theme Events — typed custom events for cross-component communication.
 *
 * Usage:
 *   document.dispatchEvent(new VariantUpdateEvent(variant, sectionId, { html }));
 *   document.addEventListener(ThemeEvents.variantUpdate, (e) => { ... });
 */

export class ThemeEvents {
  static variantSelected = 'variant:selected';
  static variantUpdate = 'variant:update';
  static cartAdd = 'cart:add';
  static cartUpdate = 'cart:update';
  static cartError = 'cart:error';
  static mediaStartedPlaying = 'media:started-playing';
  static quantitySelectorUpdate = 'quantity-selector:update';
  static megaMenuHover = 'megaMenu:hover';
  static FilterUpdate = 'filter:update';
}

/** Fired when a variant option is selected (before fetch) */
export class VariantSelectedEvent extends Event {
  /** @param {{ id: string }} resource */
  constructor(resource) {
    super(ThemeEvents.variantSelected, { bubbles: true });
    this.detail = { resource };
  }
}

/** Fired after variant update with new HTML */
export class VariantUpdateEvent extends Event {
  constructor(resource, sourceId, data) {
    super(ThemeEvents.variantUpdate, { bubbles: true });
    this.detail = {
      resource: resource || null,
      sourceId,
      data: {
        html: data.html,
        productId: data.productId,
        newProduct: data.newProduct,
      },
    };
  }
}

/** Fired when item(s) added to cart — distinct from update */
export class CartAddEvent extends Event {
  constructor(resource, sourceId, data) {
    super(ThemeEvents.cartAdd, { bubbles: true });
    this.detail = { resource, sourceId, data: { ...data } };
  }
  static eventName = ThemeEvents.cartAdd;
}

/** Fired when cart is updated (quantity change, remove) */
export class CartUpdateEvent extends Event {
  constructor(resource, sourceId, data) {
    super(ThemeEvents.cartUpdate, { bubbles: true });
    this.detail = { resource, sourceId, data: { ...data } };
  }
}

/** Fired when a cart operation fails */
export class CartErrorEvent extends Event {
  constructor(sourceId, message, description, errors) {
    super(ThemeEvents.cartError, { bubbles: true });
    this.detail = { sourceId, data: { message, errors, description } };
  }
}

/** Fired when quantity selector value changes */
export class QuantitySelectorUpdateEvent extends Event {
  constructor(quantity, cartLine) {
    super(ThemeEvents.quantitySelectorUpdate, { bubbles: true });
    this.detail = { quantity, cartLine };
  }
}

/** Fired when mega menu is hovered */
export class MegaMenuHoverEvent extends Event {
  constructor() {
    super(ThemeEvents.megaMenuHover, { bubbles: true });
  }
}

/** Fired when collection filters change */
export class FilterUpdateEvent extends Event {
  constructor(queryParams) {
    super(ThemeEvents.FilterUpdate, { bubbles: true });
    this.detail = { queryParams };
  }

  shouldShowClearAll() {
    return [...this.detail.queryParams.entries()].filter(([key]) => key.startsWith('filter.')).length > 0;
  }
}